import React, { useState } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import {
  LinkIcon,
  ChartBarIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  GlobeAltIcon,
  DocumentTextIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  ArrowDownTrayIcon,
  ArrowTopRightOnSquareIcon,
  ShieldCheckIcon,
  XCircleIcon
} from '@heroicons/react/24/outline';
import { StarIcon } from '@heroicons/react/24/solid';
import { Helmet } from 'react-helmet';
import FAQSection from '../components/FAQSection';

const BacklinkExplorer = () => {
  const [domain, setDomain] = useState('');
  const [loading, setLoading] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [filters, setFilters] = useState({
    domainAuthority: [0, 1000],
    linkType: 'all',
    status: 'all'
  });
  const [showFilters, setShowFilters] = useState(false);
  const [strategyPopup, setStrategyPopup] = useState(null);

  const handleAnalyze = async () => {
    if (!domain) {
      toast.error('Please enter a domain');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post('/api/data-for-seo/backlink-analysis', { domain });
      setAnalysis(response.data);
      toast.success('Backlink analysis complete!');
    } catch (error) {
      // Show error to user
      toast.error('Failed to fetch backlink analysis. Please try again.');
      console.error('Backlink analysis error:', error);
    } finally {
      setLoading(false);
    }
  };

  const MetricCard = ({ title, value, change, icon: Icon, color = 'indigo' }) => (
    <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-200">
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm text-gray-600">{title}</span>
        <Icon className={`h-5 w-5 text-${color}-500`} />
      </div>
      <p className="text-2xl font-bold text-gray-900">{value}</p>
      {change && (
        <div className="flex items-center mt-2">
          {change > 0 ? (
            <ArrowTrendingUpIcon className="h-4 w-4 text-green-500" />
          ) : (
            <ArrowTrendingDownIcon className="h-4 w-4 text-red-500" />
          )}
          <span className={`text-sm ml-1 ${change > 0 ? 'text-green-600' : 'text-red-600'}`}>
            {change > 0 ? '+' : ''}{change}%
          </span>
        </div>
      )}
    </div>
  );

  const BacklinkRow = ({ link }) => (
    <tr className="hover:bg-gray-50">
      <td className="px-6 py-4">
        <div>
          <p className="text-sm font-medium text-gray-900 truncate max-w-xs">{link.sourceUrl}</p>
          <p className="text-xs text-gray-500">{link.sourceDomain}</p>
        </div>
      </td>
      <td className="px-6 py-4">
        <p className="text-sm text-gray-900 truncate max-w-xs">{link.anchorText}</p>
      </td>
      <td className="px-6 py-4">
        <div className="flex items-center space-x-2">
          <span className="text-sm font-medium">{link.domainAuthority}</span>
          <div className="w-16 bg-gray-200 rounded-full h-2">
            <div
              className={`h-2 rounded-full ${
                link.domainAuthority > 700 ? 'bg-green-500' :
                link.domainAuthority > 400 ? 'bg-yellow-500' : 'bg-red-500'
              }`}
              style={{ width: `${(link.domainAuthority || 0) / 10}%` }}
            />
          </div>
        </div>
      </td>
      <td className="px-6 py-4">
        <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
          link.linkType === 'dofollow'
            ? 'bg-green-100 text-green-700'
            : 'bg-gray-100 text-gray-700'
        }`}>
          {link.linkType}
        </span>
      </td>
      <td className="px-6 py-4">
        <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
          link.status === 'active' ? 'bg-green-100 text-green-700' :
          link.status === 'new' ? 'bg-blue-100 text-blue-700' :
          'bg-red-100 text-red-700'
        }`}>
          {link.status}
        </span>
      </td>
      <td className="px-6 py-4">
        <span className={`text-sm ${
          link.spamScore < 30 ? 'text-green-600' :
          link.spamScore < 60 ? 'text-yellow-600' :
          'text-red-600'
        }`}>
          {link.spamScore}%
        </span>
      </td>
      <td className="px-6 py-4">
        <button className="text-indigo-600 hover:text-indigo-700">
          <ArrowTopRightOnSquareIcon className="h-4 w-4" />
        </button>
      </td>
    </tr>
  );

  // Apply filters to backlinks
  const filteredBacklinks = analysis?.backlinks.filter(link => {
    if (filters.linkType !== 'all' && link.linkType !== filters.linkType) {
      return false;
    }
    if (filters.status !== 'all' && link.status !== filters.status) {
      return false;
    }
    if (link.domainAuthority < filters.domainAuthority[0] || link.domainAuthority > filters.domainAuthority[1]) {
      return false;
    }
    return true;
  }) || [];


  const BACKLINK_FAQ = [
    { q: "What is a backlink and why are backlinks important for SEO?", a: "A backlink is a hyperlink from one website to another. Backlinks are one of the most important ranking factors in search engine algorithms because they serve as votes of confidence from other websites. High-quality backlinks from authoritative, relevant domains signal to Google that your content is trustworthy and valuable, directly improving your search rankings and organic visibility." },
    { q: "How does SERPfinity backlink explorer analyze link profiles?", a: "SERPfinity crawls and indexes billions of web pages to build comprehensive backlink databases. Our explorer reveals total backlinks, unique referring domains, anchor text distribution, follow vs nofollow ratios, domain authority of linking sites, link velocity trends, and toxic link identification. We provide competitive comparison features to benchmark your profile against top-ranking competitors." },
    { q: "What makes a backlink high quality versus low quality?", a: "High-quality backlinks come from authoritative, relevant websites with strong domain authority, appear within editorial content naturally, use relevant anchor text, and are from unique referring domains. Low-quality backlinks come from spammy directories, link farms, irrelevant sites, or use manipulative anchor text patterns. SERPfinity scores each backlink for quality to help you prioritize link building efforts." },
    { q: "How can I find backlink opportunities for my website?", a: "SERPfinity identifies backlink opportunities through competitor gap analysis, finding sites that link to competitors but not to you. We also discover broken link building opportunities, unlinked brand mentions, resource page targets, and guest posting prospects. Our AI analyzes content topics to recommend the most relevant and achievable link targets for your domain." },
    { q: "What is anchor text and how does it affect rankings?", a: "Anchor text is the clickable text of a hyperlink. Search engines use anchor text to understand what the linked page is about. A natural anchor text profile includes branded terms, naked URLs, generic phrases, and topic-relevant keywords. Over-optimized anchor text with exact-match keywords can trigger Google penalties. SERPfinity analyzes your anchor text distribution to ensure it appears natural." },
    { q: "How do I identify and disavow toxic backlinks?", a: "Toxic backlinks come from spammy, penalized, or irrelevant websites and can harm your rankings. SERPfinity identifies toxic links by analyzing domain spam scores, link patterns, and relevance signals. Once identified, you can create a disavow file to submit to Google Search Console, telling Google to ignore these harmful links when assessing your site authority." }
  ];

  return (
    <>
        <Helmet>
          <title>Backlink Explorer & Analyzer | Check Any Domain Backlink Profile | SERPfinity</title>
          <meta name="description" content="Analyze backlink profiles for any domain. Discover referring domains, anchor text distribution, link quality scores, and competitive backlink opportunities with SERPfinity backlink explorer." />
          <meta name="keywords" content="backlink explorer, backlink analyzer, backlink checker, referring domains, anchor text analysis, link building tool, competitor backlinks, domain backlink profile, SERPfinity" />
          <link rel="canonical" href="https://thedigitalhall.club/backlink-explorer" />
          <meta property="og:title" content="Backlink Explorer & Analyzer | SERPfinity" />
          <meta property="og:description" content="Comprehensive backlink analysis for any domain. Check referring domains, link quality, and competitor backlink strategies." />
          <meta property="og:type" content="website" />
          <meta property="og:url" content="https://thedigitalhall.club/backlink-explorer" />
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content="Backlink Explorer & Analyzer | SERPfinity" />
          <meta name="twitter:description" content="Analyze backlink profiles, discover link opportunities, and monitor competitor backlink strategies." />
          <meta name="robots" content="index, follow" />
          <script type="application/ld+json">{JSON.stringify({"@context":"https://schema.org","@type":"FAQPage","mainEntity":BACKLINK_FAQ.map(item=>({"@type":"Question","name":item.q,"acceptedAnswer":{"@type":"Answer","text":item.a}}))})}</script>
        </Helmet>
      <div className="min-h-screen bg-gray-50">
        <div className="px-6 py-6">
        {/* Header */}
        <div className="mb-8">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center">
                <LinkIcon className="h-8 w-8 text-indigo-600 mr-3" />
                Backlink Explorer
              </h1>
              <p className="text-gray-600 mt-2">
                Analyze backlinks, find opportunities, and monitor your link profile
              </p>
            </div>
            {analysis && (
              <div className="flex space-x-3">
                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className="px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center"
                >
                  <FunnelIcon className="h-4 w-4 mr-2" />
                  Filter
                </button>
                <button className="px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center">
                  <ArrowDownTrayIcon className="h-4 w-4 mr-2" />
                  Export
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Domain Input */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="flex gap-4">
            <div className="flex-1 relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                value={domain}
                onChange={(e) => setDomain(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleAnalyze()}
                placeholder="Enter domain to analyze (e.g., example.com)"
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
              />
            </div>
            <button
              onClick={handleAnalyze}
              disabled={loading}
              className="px-8 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-lg font-semibold hover:from-indigo-700 hover:to-purple-700 disabled:opacity-50"
            >
              {loading ? 'Analyzing...' : 'Analyze Backlinks'}
            </button>
          </div>
        </div>

        {/* Filter Panel */}
        {showFilters && analysis && (
          <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Filters</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Link Type</label>
                <select
                  value={filters.linkType}
                  onChange={(e) => setFilters({...filters, linkType: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#45e09f] focus:border-transparent"
                >
                  <option value="all">All Types</option>
                  <option value="dofollow">Dofollow</option>
                  <option value="nofollow">Nofollow</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                <select
                  value={filters.status}
                  onChange={(e) => setFilters({...filters, status: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#45e09f] focus:border-transparent"
                >
                  <option value="all">All Status</option>
                  <option value="active">Active</option>
                  <option value="new">New</option>
                  <option value="lost">Lost</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Domain Authority</label>
                <div className="flex space-x-2">
                  <input
                    type="number"
                    min="0"
                    max="1000"
                    value={filters.domainAuthority[0]}
                    onChange={(e) => setFilters({...filters, domainAuthority: [parseInt(e.target.value), filters.domainAuthority[1]]})}
                    className="w-1/2 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#45e09f] focus:border-transparent"
                    placeholder="Min"
                  />
                  <input
                    type="number"
                    min="0"
                    max="1000"
                    value={filters.domainAuthority[1]}
                    onChange={(e) => setFilters({...filters, domainAuthority: [filters.domainAuthority[0], parseInt(e.target.value)]})}
                    className="w-1/2 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#45e09f] focus:border-transparent"
                    placeholder="Max"
                  />
                </div>
              </div>
            </div>
            <div className="flex justify-end mt-4 space-x-3">
              <button
                onClick={() => setFilters({domainAuthority: [0, 1000], linkType: 'all', status: 'all'})}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                Reset Filters
              </button>
              <button
                onClick={() => setShowFilters(false)}
                className="px-4 py-2 bg-[#45e09f] text-[#212433] rounded-lg hover:bg-[#3ec989]"
              >
                Apply Filters
              </button>
            </div>
          </div>
        )}

        {/* Analysis Results */}
        {analysis && (
          <>
            {/* Overview Cards */}
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
              <MetricCard
                title="Total Backlinks"
                value={analysis.overview.totalBacklinks.toLocaleString()}
                change={analysis.overview.totalBacklinks ? Math.round((analysis.overview.newBacklinks30d - analysis.overview.lostBacklinks30d) / analysis.overview.totalBacklinks * 100) : 0}
                icon={LinkIcon}
              />
              <MetricCard
                title="Referring Domains"
                value={analysis.overview.referringDomains.toLocaleString()}
                change={analysis.overview.referringDomains ? Math.round((analysis.overview.newDomains30d - analysis.overview.lostDomains30d) / analysis.overview.referringDomains * 100) : 0}
                icon={GlobeAltIcon}
              />
              <MetricCard
                title="Domain Rank (0-1000)"
                value={analysis.overview.domainRank ?? 'n/a'}
                icon={ChartBarIcon}
              />
              <MetricCard
                title="Spam Score"
                value={analysis.overview.spamScore != null ? `${analysis.overview.spamScore}/100` : 'n/a'}
                icon={ShieldCheckIcon}
                color={analysis.overview.spamScore >= 60 ? 'red' : analysis.overview.spamScore >= 30 ? 'yellow' : 'green'}
              />
              <MetricCard
                title="Broken Backlinks"
                value={analysis.overview.brokenBacklinks.toLocaleString()}
                icon={ArrowTrendingDownIcon}
                color={analysis.overview.brokenBacklinks > 0 ? 'red' : 'blue'}
              />
            </div>

            {/* New/Lost Links Alert */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex items-center">
                  <CheckCircleIcon className="h-5 w-5 text-green-600 mr-2" />
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-green-900">New Backlinks (30 days)</p>
                    <p className="text-2xl font-bold text-green-700">+{analysis.overview.newBacklinks30d}</p>
                  </div>
                </div>
              </div>
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex items-center">
                  <XCircleIcon className="h-5 w-5 text-red-600 mr-2" />
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-red-900">Lost Backlinks (30 days)</p>
                    <p className="text-2xl font-bold text-red-700">-{analysis.overview.lostBacklinks30d}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Tabs */}
            <div className="bg-white rounded-lg shadow-sm">
              <div className="border-b">
                <nav className="flex -mb-px">
                  {['overview', 'backlinks', 'anchors', 'referring', 'toxic', 'opportunities'].map((tab) => (
                    <button
                      key={tab}
                      onClick={() => setActiveTab(tab)}
                      className={`py-3 px-6 border-b-2 font-medium text-sm capitalize transition-colors ${
                        activeTab === tab
                          ? 'border-indigo-500 text-indigo-600'
                          : 'border-transparent text-gray-500 hover:text-gray-700'
                      }`}
                    >
                      {tab}
                    </button>
                  ))}
                </nav>
              </div>

              <div className="p-6">
                {/* Overview Tab */}
                {activeTab === 'overview' && (
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-4">Backlink Growth Trend</h3>
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={analysis.growth?.trend || []} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                        <defs>
                          <linearGradient id="colorBacklinks" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.3}/>
                            <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0}/>
                          </linearGradient>
                          <linearGradient id="colorDomains" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#06b6d4" stopOpacity={0.3}/>
                            <stop offset="95%" stopColor="#06b6d4" stopOpacity={0}/>
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis dataKey="month" tick={{ fontSize: 12 }} stroke="#9ca3af" />
                        <YAxis tick={{ fontSize: 12 }} stroke="#9ca3af" tickFormatter={(v) => v >= 1000 ? (v/1000).toFixed(0) + 'k' : v} />
                        <Tooltip formatter={(value, name) => [value.toLocaleString(), name === 'backlinks' ? 'Backlinks' : 'Referring Domains']} />
                        <Legend />
                        <Area type="monotone" dataKey="backlinks" name="Backlinks" stroke="#8b5cf6" fillOpacity={1} fill="url(#colorBacklinks)" strokeWidth={2} />
                        <Area type="monotone" dataKey="domains" name="Referring Domains" stroke="#06b6d4" fillOpacity={1} fill="url(#colorDomains)" strokeWidth={2} />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>

                    <div className="grid grid-cols-2 gap-6 mt-6">
                      <div>
                        <h4 className="font-medium text-gray-900 mb-3">Link Type Distribution</h4>
                        <div className="space-y-2">
                          <div className="flex justify-between">
                            <span className="text-sm text-gray-600">Dofollow</span>
                            <span className="text-sm font-medium">{analysis.overview.dofollow.toLocaleString()} ({Math.round(analysis.overview.dofollow / analysis.overview.totalBacklinks * 100)}%)</span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div className="bg-green-500 h-2 rounded-full" style={{ width: `${Math.round(analysis.overview.dofollow / analysis.overview.totalBacklinks * 100)}%` }}></div>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-sm text-gray-600">Nofollow</span>
                            <span className="text-sm font-medium">{analysis.overview.nofollow.toLocaleString()} ({Math.round(analysis.overview.nofollow / analysis.overview.totalBacklinks * 100)}%)</span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div className="bg-gray-500 h-2 rounded-full" style={{ width: `${Math.round(analysis.overview.nofollow / analysis.overview.totalBacklinks * 100)}%` }}></div>
                          </div>
                        </div>
                      </div>

                      <div>
                        <h4 className="font-medium text-gray-900 mb-3">Quick Stats</h4>
                        <div className="space-y-2">
                          <div className="flex justify-between py-2 border-b">
                            <span className="text-sm text-gray-600">Avg. Referring Domain Rank</span>
                            <span className="text-sm font-medium">{analysis.overview.avgReferringRank ?? 'n/a'}</span>
                          </div>
                          <div className="flex justify-between py-2 border-b">
                            <span className="text-sm text-gray-600">Unique IPs</span>
                            <span className="text-sm font-medium">{(analysis.overview.referringIps || 0).toLocaleString()}</span>
                          </div>
                          <div className="flex justify-between py-2 border-b">
                            <span className="text-sm text-gray-600">Unique C-Class IPs</span>
                            <span className="text-sm font-medium">{(analysis.overview.referringSubnets || 0).toLocaleString()}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Backlinks Tab */}
                {activeTab === 'backlinks' && (
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Source</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Anchor Text</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Domain Rank</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Spam</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"></th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {filteredBacklinks.slice(0, 10).map((link) => (
                          <BacklinkRow key={link.id} link={link} />
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Anchors Tab */}
                {activeTab === 'anchors' && (
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-4">Anchor Text Distribution</h3>
                    <div className="space-y-3">
                      {analysis.anchorDistribution.map((anchor, idx) => (
                        <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <div className="flex-1">
                            <p className="font-medium text-gray-900">{anchor.text}</p>
                            <p className="text-sm text-gray-500">{anchor.count.toLocaleString()} backlinks</p>
                          </div>
                          <div className="flex items-center space-x-3">
                            <div className="w-32 bg-gray-200 rounded-full h-2">
                              <div className="bg-indigo-500 h-2 rounded-full" style={{ width: `${anchor.percentage}%` }}></div>
                            </div>
                            <span className="text-sm font-medium text-gray-700 w-12 text-right">{anchor.percentage}%</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Referring Domains Tab */}
                {activeTab === 'referring' && (
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-4">Top Referring Domains</h3>
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Domain</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Backlinks</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Domain Rank</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Spam Score</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Dofollow</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Action</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {analysis.topReferrers.map((ref, idx) => (
                            <tr key={idx} className="hover:bg-gray-50">
                              <td className="px-6 py-4">
                                <p className="text-sm font-medium text-gray-900">{ref.domain}</p>
                              </td>
                              <td className="px-6 py-4">
                                <p className="text-sm text-gray-900">{ref.backlinks}</p>
                              </td>
                              <td className="px-6 py-4">
                                <div className="flex items-center">
                                  <span className="text-sm font-medium">{ref.rank ?? 'n/a'}</span>
                                  <div className="ml-2 flex">
                                    {[...Array(5)].map((_, i) => (
                                      <StarIcon
                                        key={i}
                                        className={`h-3 w-3 ${i < Math.floor((ref.rank || 0) / 200) ? 'text-yellow-400' : 'text-gray-300'}`}
                                      />
                                    ))}
                                  </div>
                                </div>
                              </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center">
                      <div className="w-16 bg-gray-200 rounded-full h-2 mr-2">
                        <div
                          className={`h-2 rounded-full ${ref.spamScore > 70 ? 'bg-red-500' : ref.spamScore > 40 ? 'bg-yellow-500' : 'bg-green-500'}`}
                          style={{ width: `${ref.spamScore || 0}%` }}
                        />
                      </div>
                      <span className="text-sm text-gray-700">{ref.spamScore || 0}</span>
                    </div>
                  </td>
                              <td className="px-6 py-4">
                                <span className="text-sm text-gray-900">{(ref.dofollow || 0).toLocaleString()}</span>
                              </td>
                              <td className="px-6 py-4">
                                <button className="text-indigo-600 hover:text-indigo-700 text-sm">View Links</button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Toxic Links Tab */}
                {activeTab === 'toxic' && (
                  <div>
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                      <div className="flex items-start">
                        <ExclamationTriangleIcon className="h-5 w-5 text-red-600 mr-2 mt-0.5" />
                        <div>
                          <h3 className="text-sm font-semibold text-red-900">Toxic Link Alert</h3>
                          <p className="text-sm text-red-700 mt-1">
                            Found {analysis.toxicLinks.total} links from referring domains with a spam score of 10+ (checked {analysis.toxicLinks.sampleSize} referring domains)
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-3 gap-4 mb-6">
                      <div className="bg-red-100 rounded-lg p-4">
                        <p className="text-sm font-medium text-red-900">High Risk</p>
                        <p className="text-2xl font-bold text-red-700">{analysis.toxicLinks.highRisk}</p>
                      </div>
                      <div className="bg-yellow-100 rounded-lg p-4">
                        <p className="text-sm font-medium text-yellow-900">Medium Risk</p>
                        <p className="text-2xl font-bold text-yellow-700">{analysis.toxicLinks.mediumRisk}</p>
                      </div>
                      <div className="bg-gray-100 rounded-lg p-4">
                        <p className="text-sm font-medium text-gray-900">Low Risk</p>
                        <p className="text-2xl font-bold text-gray-700">{analysis.toxicLinks.lowRisk}</p>
                      </div>
                    </div>

                    <h3 className="font-semibold text-gray-900 mb-3">Most Toxic Domains</h3>
                    <div className="space-y-3">
                      {analysis.toxicLinks.topToxicDomains.map((domain, idx) => (
                        <div key={idx} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
                          <div>
                            <p className="font-medium text-gray-900">{domain.domain}</p>
                            <p className="text-sm text-gray-500">{domain.backlinks} toxic backlinks</p>
                          </div>
                          <div className="flex items-center space-x-3">
                            <div className="text-right">
                              <p className="text-sm text-gray-500">Toxicity Score</p>
                              <p className="text-lg font-bold text-red-600">{domain.toxicity}%</p>
                            </div>
                            <button onClick={() => toast.success(`${domain.domain} added to disavow list`)} className="px-3 py-1 bg-red-600 text-white rounded text-sm hover:bg-red-700">
                              Disavow
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>

                    <button onClick={() => { const content = analysis.toxicLinks.topToxicDomains.map(t => 'domain:' + t.domain).join('\n'); const blob = new Blob(['# Disavow file\n# Generated by SERPfinity\n\n' + content], {type: 'text/plain'}); const url = URL.createObjectURL(blob); const a = document.createElement('a'); a.href = url; a.download = 'disavow-' + domain + '.txt'; a.click(); URL.revokeObjectURL(url); toast.success('Disavow file downloaded!'); }} className="mt-4 w-full py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium">
                      Generate Disavow File
                    </button>
                  </div>
                )}

                {/* Opportunities Tab */}
                {activeTab === 'opportunities' && (
                  <div className="space-y-6">
                    {analysis.opportunities.map((opp, idx) => (
                      <div key={idx} className="p-6 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-200">
                        <div className="flex justify-between items-start">
                          <div>
                            <h3 className="font-semibold text-gray-900 mb-2">{opp.title}</h3>
                            <p className="text-sm text-gray-600 mb-3">Example domains:</p>
                            <div className="flex flex-wrap gap-2">
                              {opp.domains.map((domain, i) => (
                                <span key={i} className="px-2 py-1 bg-white rounded text-sm text-gray-700">
                                  {domain}
                                </span>
                              ))}
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="text-sm text-gray-600">Potential Impact</p>
                            <p className="font-semibold text-green-600">{opp.potentialImpact}</p>
                            <button onClick={() => setStrategyPopup(opp)} className="mt-2 px-4 py-2 bg-indigo-600 text-white rounded text-sm hover:bg-indigo-700">
                              View Strategy
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}

                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <h3 className="font-semibold text-blue-900 mb-2">Link Gap Analysis</h3>
                      <p className="text-blue-700 mb-3">Your top competitors have links from domains you don't:</p>
                      <div className="space-y-2">
                        {analysis.competitors.map((comp, idx) => (
                          <div key={idx} className="flex justify-between items-center">
                            <span className="text-sm font-medium text-blue-900">{comp.domain}</span>
                            <div className="text-sm text-blue-700">
                              <span className="font-medium">{comp.sharedDomains}</span> shared referring domains /
                              <span className="font-bold text-blue-900 ml-1">rank {comp.rank ?? 'n/a'}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </>
        )}

        {/* Empty State */}

      {/* Strategy Popup Modal */}
      {strategyPopup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setStrategyPopup(null)}>
          <div className="bg-white rounded-xl p-6 max-w-lg w-full mx-4" onClick={e => e.stopPropagation()}>
            <h3 className="text-xl font-bold mb-4">{strategyPopup.type} Strategy</h3>
            <div className="space-y-3">
              <div><span className="font-semibold">Type:</span> {strategyPopup.type}</div>
              <div><span className="font-semibold">Target Domains:</span> {strategyPopup.targetDomains}</div>
              <div><span className="font-semibold">Potential Impact:</span> <span className="text-green-600 font-semibold">{strategyPopup.potentialImpact}</span></div>
              <div className="mt-4">
                <p className="font-semibold mb-2">Recommended Steps:</p>
                <ol className="list-decimal list-inside space-y-1 text-sm text-gray-600">
                  <li>Identify target pages on competitor domains</li>
                  <li>Create superior content for those topics</li>
                  <li>Reach out to webmasters for link placement</li>
                  <li>Monitor new backlinks and adjust strategy</li>
                </ol>
              </div>
            </div>
            <button onClick={() => setStrategyPopup(null)} className="mt-6 w-full py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700">Got It</button>
          </div>
        </div>
      )}

        {!analysis && !loading && (
          <div className="bg-white rounded-lg shadow-sm p-12 text-center">
            <LinkIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">Explore Your Backlink Profile</h3>
            <p className="text-gray-600 max-w-md mx-auto">
              Enter any domain to analyze its backlink profile, find toxic links, and discover link building opportunities.
            </p>
          </div>
        )}
        </div>
      </div>
        <FAQSection items={BACKLINK_FAQ} />
    </>
  );
};

export default BacklinkExplorer;
